import type React from "react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { selectEditMode } from "../app/appSlice";

import { useAppSelector } from "@/app/hooks";
import {
  selectCurrentBuilding,
  selectCurrentBuildingGeoJSON,
} from "@/features/cartography/cartographySlice.selectors";
import { selectFlightsWithPois } from "@/features/flight_information/flightInformationSlice.selectors";
import { IMDF_WORKER_ID } from "@/libs/imdf/imdf";
import { IMDF_EVENTS } from "@/libs/imdf/types";
import IMDFWorker from "../../libs/imdf/processing/worker?worker";
import {
  type InitMessagePayload,
  WORKER_EVENTS as SEARCH_WORKER_EVENTS,
  SearchableItem,
  SearchResultType,
} from "../explorer_panel/search/types";
import SearchWorker from "../explorer_panel/search/worker?worker";
import { SEARCH_WORKER_ID } from "./constants";
import { createBatches } from "./utils";
import WorkerController from "./WorkerController";

export enum WorkerStatus {
  READY,
  NOT_READY,
}

export type WorkerSubscriptionListenerCallback = ({
  worker,
}: {
  worker: WorkerController;
}) => void;

export type WorkerSubscriptionListener = ({
  id,
  callback,
}: {
  id: string;
  callback: WorkerSubscriptionListenerCallback;
}) => void;

export type WorkerContextType = {
  initWorker: ({
    id,
    workerClass,
    persistent,
    create,
    onRegistered,
  }: {
    id: string;
    workerClass?: any;
    persistent?: boolean;
    create?: boolean;
    onRegistered?: (worker: WorkerController) => Promise<WorkerStatus>;
  }) => Promise<WorkerController>;
  terminateWorker: (workerClass: any) => void;
  subscribeToWorkerLifecycle: WorkerSubscriptionListener;
  unSubscribeFromWorkerLifecycle: WorkerSubscriptionListener;
};

const WorkerContext = createContext<WorkerContextType>(null);

export const WorkerProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Redux
  const currentBuilding = useAppSelector(selectCurrentBuilding);
  const currentBuildingGeoJSON = useAppSelector(selectCurrentBuildingGeoJSON);
  const flightsWithPOIs = useAppSelector(selectFlightsWithPois);
  const editMode = useAppSelector(selectEditMode);

  // State
  const workers = useRef<Record<string, WorkerController>>({});
  const workerLifecycleListeners = useRef<
    Record<string, WorkerSubscriptionListenerCallback[]>
  >({});
  // Search worker state
  const [searchDataReady, setSearchDataReady] = useState<
    Record<SearchResultType, boolean>
  >({
    [SearchResultType.POI]: false,
    [SearchResultType.FLIGHT]: false,
    [SearchResultType.GEOFENCE]: false,
  });

  const updateWorkerStatus = useCallback(
    (workerId: string, status: WorkerStatus) => {
      if (!workers.current[workerId]) return;
      workers.current[workerId].setWorkerStatus(status);
      notifyLifecycleListeners(workerId);
    },
    [],
  );

  const notifyLifecycleListeners = useCallback((workerId: string) => {
    Object.values(workerLifecycleListeners.current[workerId] ?? []).forEach(
      (callback) => {
        callback({ worker: workers.current[workerId] });
      },
    );
  }, []);

  const subscribeToWorkerLifecycle: WorkerContextType["subscribeToWorkerLifecycle"] =
    useCallback(({ id, callback }) => {
      // Check if worker already exists and is initialized
      const existingWorker = workers.current[id];
      if (existingWorker) {
        // Call callback immediately with current worker status
        callback({
          worker: existingWorker,
        });
      }
      // Initialize listener for future changes
      if (!workerLifecycleListeners.current[id]) {
        workerLifecycleListeners.current[id] = [];
      }
      workerLifecycleListeners.current[id].push(callback);
    }, []);

  const unSubscribeFromWorkerLifecycle: WorkerContextType["unSubscribeFromWorkerLifecycle"] =
    useCallback(({ id, callback }) => {
      if (workerLifecycleListeners.current[id]) {
        workerLifecycleListeners.current[id] = workerLifecycleListeners.current[
          id
        ].filter((listener) => listener !== callback);
      }
    }, []);

  /**
   * Creates, registers and optionally saves a worker manager instance. In
   * case some initialization has to take place, then it also notifies
   * any registered listeners.
   */
  const initWorker: WorkerContextType["initWorker"] = useCallback(
    ({ id, workerClass, persistent = false, onRegistered }) => {
      return new Promise((resolve, reject) => {
        if (!workerClass) {
          reject("Cannot instantiate worker with undefined class");
        }
        // Create register and save worker
        if (persistent) {
          workers.current[id] = new WorkerController({
            workerClass,
          });
          workers.current[id].registerWorker();
          // If some initialization task needs to take place
          if (onRegistered) {
            onRegistered(workers.current[id]).then((workerStatus) => {
              updateWorkerStatus(id, workerStatus);
              resolve(workers.current[id]);
            });
          } else {
            // If no initialization is needed, mark as ready
            updateWorkerStatus(id, WorkerStatus.READY);
            resolve(workers.current[id]);
          }
        } else {
          // Create and register worker
          const tempWorker = new WorkerController({ workerClass });
          tempWorker.registerWorker();
          resolve(tempWorker);
        }
      });
    },
    [workers, updateWorkerStatus],
  );

  /**
   * Unregisters and deletes a worker from the state
   */
  const terminateWorker: WorkerContextType["terminateWorker"] = useCallback(
    (id) => {
      if (workers.current[id]) {
        workers.current[id].unregisterWorker();
        updateWorkerStatus(id, WorkerStatus.NOT_READY);
        delete workers.current[id];
      }
    },
    [],
  );

  // Setup search worker
  useEffect(() => {
    initWorker({
      id: SEARCH_WORKER_ID,
      onRegistered: async (worker) => {
        return worker
          .postMessageToWorker<void>({
            type: SEARCH_WORKER_EVENTS.INIT,
          })
          .then(() => {
            setSearchDataReady({
              [SearchResultType.POI]: false,
              [SearchResultType.GEOFENCE]: false,
              [SearchResultType.FLIGHT]: false,
            });

            // Wait until all data is ready
            return WorkerStatus.NOT_READY;
          });
      },
      persistent: true,
      workerClass: SearchWorker,
    });

    return () => {
      terminateWorker(SEARCH_WORKER_ID);
    };
  }, [currentBuilding?.id]);

  useEffect(() => {
    if (!currentBuilding?.pois) return;
    if (!workers.current[SEARCH_WORKER_ID]) return;

    // Clear previous POI information
    setSearchDataReady((p) => ({ ...p, [SearchResultType.POI]: false }));
    workers.current[SEARCH_WORKER_ID].postMessageToWorker<void>({
      payload: {
        type: SearchResultType.POI,
      } satisfies InitMessagePayload,
      type: SEARCH_WORKER_EVENTS.INIT,
    });

    // Populate with updated POI information
    workers.current[SEARCH_WORKER_ID]
      .postBatchedMessageToWorker<void>({
        payload: createBatches(
          currentBuilding.pois.toArray().map(SearchableItem.fromPoi),
          200,
        ).map((batch) => ({
          items: batch,
          type: SearchResultType.POI,
        })),
        type: SEARCH_WORKER_EVENTS.POPULATE,
      })
      .then(() => {
        setSearchDataReady((p) => ({ ...p, [SearchResultType.POI]: true }));
      });
  }, [currentBuilding?.pois]);

  useEffect(() => {
    if (!flightsWithPOIs) return;
    if (!workers.current[SEARCH_WORKER_ID]) return;

    // Clear previous flight information
    setSearchDataReady((p) => ({ ...p, [SearchResultType.FLIGHT]: false }));
    workers.current[SEARCH_WORKER_ID].postMessageToWorker<void>({
      payload: {
        type: SearchResultType.FLIGHT,
      } satisfies InitMessagePayload,
      type: SEARCH_WORKER_EVENTS.INIT,
    });

    // TODO JLAQ - Review this, should not have duplicate flights
    // returned by the API

    // Remove duplicates based on flight.id field
    const uniqueFlightsWithPOIs = flightsWithPOIs.filter(
      (flight, index, self) =>
        index === self.findIndex((t) => t.flight.id === flight.flight.id),
    );

    workers.current[SEARCH_WORKER_ID]
      .postBatchedMessageToWorker<void>({
        payload: createBatches(
          uniqueFlightsWithPOIs.map(SearchableItem.fromFlightWithPOI),
          200,
        ).map((batch) => ({
          items: batch,
          type: SearchResultType.FLIGHT,
        })),
        type: SEARCH_WORKER_EVENTS.POPULATE,
      })
      .then(() => {
        setSearchDataReady((p) => ({ ...p, [SearchResultType.FLIGHT]: true }));
      });
  }, [flightsWithPOIs, currentBuilding?.pois]);

  // This effect controls when the search worker is ready to be used
  // The worker is ready when POIs and flight data (if it exists) has been sent to the worker
  useEffect(() => {
    if (!workers.current[SEARCH_WORKER_ID]) return;

    // POIs must always be initialized
    if (!searchDataReady[SearchResultType.POI]) {
      updateWorkerStatus(SEARCH_WORKER_ID, WorkerStatus.NOT_READY);
      return;
    }

    // Flights must be initialized if they exist
    if (flightsWithPOIs?.length > 0 && !searchDataReady[SearchResultType.FLIGHT]) {
      updateWorkerStatus(SEARCH_WORKER_ID, WorkerStatus.NOT_READY);
      return;
    }

    updateWorkerStatus(SEARCH_WORKER_ID, WorkerStatus.READY);
  }, [searchDataReady, flightsWithPOIs, updateWorkerStatus]);

  /**
   * Initialize and populate the necessary data (geojson and theme) on a persistent worker each time the
   * geojson on the building changes. Note that we do not re-initialize the worker while on edit mode, as
   * the frequency of the changes on the geojson undoes the performance advantages of using a worker.
   * Whenever the data has been populated, all the components  that depend
   * on this worker will be notified.
   **/
  useEffect(() => {
    if (!currentBuildingGeoJSON || editMode) {
      return;
    }

    initWorker({
      id: IMDF_WORKER_ID,
      onRegistered: async (worker) => {
        // Whenever this finishes, all registered listeners will be notified
        return worker
          .postMessageToWorker<void>({
            payload: {
              imdf: currentBuildingGeoJSON,
              theme: currentBuildingGeoJSON.theme,
            },
            type: IMDF_EVENTS.INIT,
          })
          .then(() => {
            return WorkerStatus.READY;
          });
      },
      persistent: true,
      workerClass: IMDFWorker,
    });

    return () => {
      terminateWorker(IMDF_WORKER_ID);
    };
  }, [editMode, currentBuildingGeoJSON?.lastChangeTimestamp]);

  return (
    <WorkerContext.Provider
      value={{
        initWorker,
        subscribeToWorkerLifecycle,
        terminateWorker,
        unSubscribeFromWorkerLifecycle,
      }}
    >
      {children}
    </WorkerContext.Provider>
  );
};

// Custom hook to use workers
export const useWorkerFactory = () => {
  const context = useContext(WorkerContext);
  if (!context) {
    throw new Error("useWorker must be used within a WorkerProvider");
  }
  return context;
};
